import React, {useState, useEffect} from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { selectAllRoutes, selectTomorrowAllRoutes, selectAllActiveDrivers, selectAllActiveVehicles, selectHistoryRoutes } from "./../../store";
import PersonnelSection from "./PersonnelSection";
import { AuthService, CustomerService, SignatureRequestService, EmployeeService, TransRoutesService, EventsService, DailyRoutesTemplateService, CustomerAbsenseService } from "../../services";
import { findTemplateRouteByParamId } from "./dailyTemplateRouteUtils";
import { findExternalDriverRecordByExternalUserId } from "../../shared/utils/driverDropdownOptions";
import {
  ROUTE_STATUS,
  PERSONAL_ROUTE_STATUS,
  getCustomerDayStatus,
  indexAbsencesByCustomerId,
} from "../../shared";
import moment from 'moment';
import { Breadcrumb, Tabs, Tab, Dropdown, Spinner, Modal, Button } from "react-bootstrap";
import { Download, Pencil } from "react-bootstrap-icons";
import RouteCustomerEditor from "./RouteCustomerEditor";
import { mergeChecklistResultWithVehicleChecklist } from "../../utils/routeChecklist";

const formatRouteClockTime = (value) =>
  value ? moment(value).format("h:mm A") : "";

const RouteView = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const dailyTemplateId = searchParams.get("dailyTemplateId");
  const allRoutes = useSelector(selectAllRoutes);
  const tomorrowRoutes = useSelector(selectTomorrowAllRoutes);
  const historyRoutes = useSelector(selectHistoryRoutes);
  const drivers = useSelector(selectAllActiveDrivers);
  const vehicles = useSelector(selectAllActiveVehicles);
  const currentRouteFromRedux =
    allRoutes.find((item) => item.id === params.id) ||
    tomorrowRoutes.find((item) => item.id === params.id) ||
    historyRoutes.find((item) => item.id === params.id);
  const [templateDoc, setTemplateDoc] = useState(null);
  const [templateRoute, setTemplateRoute] = useState(undefined);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateLoadError, setTemplateLoadError] = useState(null);
  const currentRoute = dailyTemplateId ? templateRoute : currentRouteFromRedux;
  const currentVehicle = vehicles.find(item => item.id === currentRoute?.vehicle );
  const currentDriver = drivers.find(item => item.id === currentRoute?.driver);
  const [fallbackDriver, setFallbackDriver] = useState(undefined);
  const [showVehicleDetails, setShowVehicleDetails] = useState(false);
  const [showResetSignatureConfirmModal, setShowResetSignatureConfirmModal] = useState(false);
  const [isResettingSignature, setIsResettingSignature] = useState(false);
  const [signature, setSignature] = useState(undefined);
  const [signatureRequest, setSignatureRequest] = useState(undefined);
  const [routeStatusValue, setRouteStatusValue] = useState('');
  const [routeViewTab, setRouteViewTab] = useState('routeOverview');
  const [groupActionsHost, setGroupActionsHost] = useState(null);
  const [isSavingRouteStatus, setIsSavingRouteStatus] = useState(false);
  const [latestRouteForStatus, setLatestRouteForStatus] = useState(undefined);
  const [routeSnapshot, setRouteSnapshot] = useState(undefined);
  const [customerMetaById, setCustomerMetaById] = useState(new Map());
  const [leaveCustomerIds, setLeaveCustomerIds] = useState([]);
  const paramsQuery = new URLSearchParams(window.location.search);
  const scheduleDate = paramsQuery.get('dateSchedule');

  const navigate = useNavigate();

  useEffect(() => {
    if (!dailyTemplateId || !params.id) {
      setTemplateDoc(null);
      setTemplateRoute(undefined);
      setTemplateLoadError(null);
      setTemplateLoading(false);
      return;
    }
    let cancelled = false;
    setTemplateLoading(true);
    setTemplateLoadError(null);
    setTemplateRoute(undefined);
    DailyRoutesTemplateService.getDailyRoutesTemplate(dailyTemplateId)
      .then((res) => {
        if (cancelled) return;
        const doc = res?.data;
        setTemplateDoc(doc);
        const found = findTemplateRouteByParamId(doc, params.id);
        if (!found) {
          setTemplateLoadError("This route was not found in the template.");
          setTemplateRoute(undefined);
        } else {
          setTemplateRoute(found);
        }
      })
      .catch(() => {
        if (!cancelled) setTemplateLoadError("Failed to load daily route template.");
      })
      .finally(() => {
        if (!cancelled) setTemplateLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dailyTemplateId, params.id]);
  const resolvedDriverId = currentDriver?.id || fallbackDriver?.id || currentRoute?.driver;
  const resolvedDriverName =
    currentDriver?.name || fallbackDriver?.name || fallbackDriver?.username || '';
  const isSignatureRequestActive = signatureRequest?.status === 'active';
  const routeStatusOptions = [
    { value: '', label: 'Not Started' },
    { value: ROUTE_STATUS.ENROUTE, label: 'En Route' },
    { value: ROUTE_STATUS.ENROUTE_TO_CENTER, label: 'En Route To Center' },
    { value: ROUTE_STATUS.DROPPED_OFF_ALL, label: 'Dropped Off All' },
    { value: ROUTE_STATUS.SIGN_OFF, label: 'Signed Off' },
    { value: ROUTE_STATUS.UNEXPECTED_ABSENT, label: 'Unexpected Absent' },
  ];
  const mergeRouteCustomerMeta = (route) => {
    if (!route || !Array.isArray(route?.route_customer_list) || customerMetaById.size === 0) {
      return route;
    }
    return Object.assign({}, route, {
      route_customer_list: route.route_customer_list.map((customerInRoute) => {
        const customerMeta = customerMetaById.get(customerInRoute?.customer_id);
        if (!customerMeta) return customerInRoute;
        return Object.assign({}, customerInRoute, {
          customer_program_type: customerMeta.program_type || '',
          customer_pay_source: customerMeta.pay_source || ''
        });
      })
    });
  };
  const routeForStatusView = mergeRouteCustomerMeta(latestRouteForStatus || routeSnapshot || currentRoute);
  const routeForAssignmentView = routeSnapshot || currentRoute;
  const routeForChecklistDisplay = routeSnapshot || currentRoute;
  const mergedChecklistForDisplay = mergeChecklistResultWithVehicleChecklist(
    currentVehicle?.checklist,
    routeForChecklistDisplay?.checklist_result
  );
  const canEditRoutes = dailyTemplateId
    ? AuthService.canEditRouteTemplates()
    : AuthService.canAddOrEditRoutes();
  const closeModal = () => {
    setShowVehicleDetails(false);
  }
  const openModal = () => {
    setShowVehicleDetails(true);
  }
  const openResetSignatureConfirmModal = () => {
    setShowResetSignatureConfirmModal(true);
  }
  const closeResetSignatureConfirmModal = () => {
    if (isResettingSignature) return;
    setShowResetSignatureConfirmModal(false);
  }
  const getRelatedOutboundRoutesForThisView = () => {
    if (allRoutes.find(item => item.id === params.id)) {
      return allRoutes.filter(item => item.type==='outbound');
    }
    if (tomorrowRoutes.find(item => item.id === params.id)) {
      return tomorrowRoutes.filter(item => item.type==='outbound');
    }
    if (historyRoutes.find(item => item.id === params.id)) {
      return historyRoutes.filter(item => item.type==='outbound');
    }
  }
  const directToDashboad = () => {
    if (dailyTemplateId) {
      navigate(`/trans-routes/daily-templates/view/${dailyTemplateId}`);
      return;
    }
    const qs = new URLSearchParams();
    qs.set('dateSchedule', moment(currentRoute?.schedule_date).format('YYYY-MM-DD'));
    const se = paramsQuery.get('scheduleEdit');
    if (se === '1' || `${se || ''}`.toLowerCase() === 'true') {
      qs.set('scheduleEdit', '1');
    }
    navigate(`/trans-routes/dashboard?${qs.toString()}`);
  };
  const edit = (editSection) => {
    if (!canEditRoutes) return;
    if (!currentRoute?.id) return;
    const qs = new URLSearchParams();
    if (dailyTemplateId) {
      qs.set("dailyTemplateId", dailyTemplateId);
    } else {
      const dateQs =
        scheduleDate ||
        (currentRoute?.schedule_date ? moment(currentRoute.schedule_date).format('YYYY-MM-DD') : '');
      if (dateQs) qs.set('dateSchedule', dateQs);
      const se = paramsQuery.get('scheduleEdit');
      if (se === '1' || `${se || ''}`.toLowerCase() === 'true') {
        qs.set('scheduleEdit', '1');
      }
    }
    qs.set('editSection', editSection);
    navigate(`/trans-routes/edit/${currentRoute.id}?${qs.toString()}`);
  };
  const deleteFile = () => {
    if (signature) {
      const dateArr = moment(currentRoute?.schedule_date)?.format('MM/DD/YYYY')?.split('/') || [];
      CustomerService.deleteFile({name: `${currentRoute?.id}_${currentRoute?.driver}_${dateArr[0]}_${dateArr[1]}`}).then(data => {
        setSignature(undefined);
      })
    }
  }
  const generateSignatureRequest = () => {
    if (!resolvedDriverId) {
      window.alert('Driver is not assigned for this route.');
      return;
    }
    SignatureRequestService.createNewSignatureRequest({
      driver_id: resolvedDriverId,
      driver_name: resolvedDriverName,
      route_id: currentRoute?.id,
      route_date: currentRoute?.schedule_date,
      route_name: currentRoute?.name,
      status: 'active',
    }).then((data) => {
      setSignatureRequest(data.data);
    })
  }
  const resetSignatureForResign = async () => {
    if (!currentRoute?.id || !currentRoute?.schedule_date) return;
    if (!resolvedDriverId && !signatureRequest?.driver_id) {
      window.alert('Driver is not assigned for this route.');
      return;
    }
    setIsResettingSignature(true);
    try {
      const dateArr = moment(currentRoute?.schedule_date)?.format('MM/DD/YYYY')?.split('/') || [];
      const month = dateArr[0];
      const day = dateArr[1];
      const candidateDriverIds = Array.from(new Set([
        resolvedDriverId,
        currentRoute?.driver,
        routeSnapshot?.driver,
        signatureRequest?.driver_id
      ].filter(Boolean)));
      await Promise.all(candidateDriverIds.map((driverId) => {
        const signatureKey = `${currentRoute?.id}_${driverId}_${month}_${day}`;
        return CustomerService.deleteFile({ name: signatureKey }).catch(() => null);
      }));

      if (signatureRequest?.id) {
        await SignatureRequestService.updateSignatureRequest(signatureRequest.id, { status: 'active' });
        const refreshedRequest = await SignatureRequestService.getSignatureRequestById(signatureRequest.id);
        setSignatureRequest(refreshedRequest?.data);
      } else {
        const newRequest = await SignatureRequestService.createNewSignatureRequest({
          driver_id: resolvedDriverId || currentRoute?.driver,
          driver_name: resolvedDriverName,
          route_id: currentRoute?.id,
          route_date: currentRoute?.schedule_date,
          route_name: currentRoute?.name,
          status: 'active',
        });
        setSignatureRequest(newRequest?.data);
      }
      setSignature(undefined);
      setShowResetSignatureConfirmModal(false);
      window.alert('Signature has been reset. Driver can sign again.');
    } catch (error) {
      console.error('Error resetting signature:', error);
      window.alert('Failed to reset signature. Please try again.');
    } finally {
      setIsResettingSignature(false);
    }
  }
  const saveRouteStatus = async () => {
    if (dailyTemplateId) return;
    if (!currentRoute?.id || isSavingRouteStatus) return;
    try {
      setIsSavingRouteStatus(true);
      const nextStatus = routeStatusValue ? [routeStatusValue] : [];
      await TransRoutesService.updateRoute(currentRoute.id, { status: nextStatus });
      await refreshRouteStatusData();
      window.alert('Route status updated successfully.');
    } catch (error) {
      console.error('Error updating route status:', error);
      window.alert('Failed to update route status.');
    } finally {
      setIsSavingRouteStatus(false);
    }
  };

  const refreshRouteStatusData = async () => {
    if (!currentRoute?.id) return;
    if (dailyTemplateId) {
      try {
        const res = await DailyRoutesTemplateService.getDailyRoutesTemplate(dailyTemplateId);
        const doc = res?.data;
        setTemplateDoc(doc);
        const found = findTemplateRouteByParamId(doc, params.id);
        if (found) {
          setTemplateRoute(found);
          setLatestRouteForStatus(found);
        }
      } catch (e) {
        console.error(e);
      }
      return;
    }
    try {
      const response = await TransRoutesService.getRoute(currentRoute.id);
      if (response?.data) {
        setLatestRouteForStatus(response.data);
      }
    } catch (error) {
      console.error('Error refreshing route status data:', error);
    }
  };

  useEffect(() => {
    const currentStatus = Array.isArray(currentRoute?.status) ? currentRoute.status[0] : '';
    setRouteStatusValue(currentStatus || '');
  }, [currentRoute?.id, currentRoute?.status]);

  useEffect(() => {
    setLatestRouteForStatus(undefined);
  }, [currentRoute?.id]);

  useEffect(() => {
    if (dailyTemplateId) {
      if (templateRoute) {
        setRouteSnapshot(templateRoute);
      } else {
        setRouteSnapshot(undefined);
      }
      return;
    }
    const routeId = currentRoute?.id || params?.id;
    if (!routeId) return;
    TransRoutesService.getRoute(routeId)
      .then((response) => {
        if (response?.data) {
          setRouteSnapshot(response.data);
        }
      })
      .catch(() => {
        setRouteSnapshot(undefined);
      });
  }, [dailyTemplateId, templateRoute, currentRoute?.id, params?.id]);

  useEffect(() => {
    CustomerService.getAllCustomers()
      .then((res) => {
        const nextMap = new Map();
        (res?.data || []).forEach((customer) => {
          nextMap.set(customer?.id, {
            program_type: customer?.program_type || '',
            pay_source: customer?.pay_source || '',
            days_of_week: customer?.days_of_week || [],
            name: customer?.name || '',
            name_cn: customer?.name_cn || '',
            id: customer?.id,
          });
        });
        setCustomerMetaById(nextMap);
      })
      .catch(() => {
        setCustomerMetaById(new Map());
      });
  }, []);

  useEffect(() => {
    const scheduleDateValue =
      currentRoute?.schedule_date || templateDoc?.template_date || "";
    if (!scheduleDateValue || customerMetaById.size === 0) {
      setLeaveCustomerIds([]);
      return;
    }

    let cancelled = false;
    CustomerAbsenseService.getAll()
      .catch(() => ({ data: [] }))
      .then((absencesRes) => {
      if (cancelled) return;
      const absencesByCustomerId = indexAbsencesByCustomerId(absencesRes?.data || []);
      const context = { absencesByCustomerId };

      const routeCustomerIds = new Set();
      (currentRoute?.route_customer_list || []).forEach((item) => {
        if (item?.customer_id) routeCustomerIds.add(`${item.customer_id}`);
        if (item?.customer_route_status === PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT && item?.customer_id) {
          routeCustomerIds.add(`${item.customer_id}`);
        }
      });

      const leaveIds = [];
      routeCustomerIds.forEach((customerId) => {
        const profile = customerMetaById.get(customerId) || { id: customerId };
        const status = getCustomerDayStatus(profile, scheduleDateValue, context);
        // Scheduled-absence highlight: absences + days_of_week default leave
        if (status.status === "leave") {
          leaveIds.push(customerId);
        }
      });

      // Also mark explicit scheduled-absent rows on the route.
      (currentRoute?.route_customer_list || []).forEach((item) => {
        if (
          item?.customer_route_status === PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT &&
          item?.customer_id &&
          !leaveIds.includes(`${item.customer_id}`)
        ) {
          leaveIds.push(`${item.customer_id}`);
        }
      });

      setLeaveCustomerIds(leaveIds);
    }).catch(() => {
      if (!cancelled) setLeaveCustomerIds([]);
    });

    return () => {
      cancelled = true;
    };
  }, [
    currentRoute?.schedule_date,
    currentRoute?.route_customer_list,
    templateDoc?.template_date,
    customerMetaById,
  ]);

  useEffect(() => {
    if (!currentRoute?.driver || currentDriver?.id) {
      setFallbackDriver(undefined);
      return;
    }
    let cancelled = false;
    const driverId = currentRoute.driver;

    const applyExternalFromPermissions = () =>
      EmployeeService.getExternalUserPermissionsList(EventsService.site)
        .then((permRes) => {
          if (cancelled) return;
          const ext = findExternalDriverRecordByExternalUserId(permRes?.data || [], driverId);
          setFallbackDriver(ext || undefined);
        })
        .catch(() => {
          if (!cancelled) setFallbackDriver(undefined);
        });

    EmployeeService.getEmployee(driverId)
      .then((data) => {
        if (cancelled) return;
        const emp = data?.data;
        if (emp && (emp.name || emp.username)) {
          setFallbackDriver(emp);
          return;
        }
        return applyExternalFromPermissions();
      })
      .catch(() => {
        if (!cancelled) applyExternalFromPermissions();
      });

    return () => {
      cancelled = true;
    };
  }, [currentRoute?.driver, currentDriver?.id]);

  useEffect(() => {
    if (dailyTemplateId) {
      setSignature(undefined);
      setSignatureRequest(undefined);
      return;
    }
    let isMounted = true;
    const loadSignatureAndRequest = async () => {
      if (!currentRoute?.id || !currentRoute?.schedule_date) {
        if (isMounted) {
          setSignature(undefined);
          setSignatureRequest(undefined);
        }
        return;
      }
      const routeId = currentRoute.id;
      const toDateTokens = (dateValue) => {
        const tokenSet = new Set();
        const localFormatted = moment(dateValue)?.format('MM/DD/YYYY');
        const utcFormatted = moment.utc(dateValue)?.format('MM/DD/YYYY');
        [localFormatted, utcFormatted].forEach((formatted) => {
          if (!formatted || formatted.toLowerCase().includes('invalid')) return;
          const [month, day] = formatted.split('/');
          if (month && day) {
            tokenSet.add(`${month}_${day}`);
          }
        });
        return Array.from(tokenSet).map((token) => token.split('_'));
      };

      const tryLoadSignature = async (driverIds = [], dateValues = []) => {
        const uniqueDriverIds = Array.from(new Set((driverIds || []).filter(Boolean)));
        const datePairs = [];
        (dateValues || []).forEach((dateValue) => {
          toDateTokens(dateValue).forEach((pair) => datePairs.push(pair));
        });
        if (datePairs.length === 0) {
          toDateTokens(currentRoute.schedule_date).forEach((pair) => datePairs.push(pair));
        }
        const uniqueDatePairs = Array.from(new Set(datePairs.map((pair) => `${pair[0]}_${pair[1]}`)))
          .map((token) => token.split('_'));
        for (const driverId of uniqueDriverIds) {
          for (const [month, day] of uniqueDatePairs) {
            try {
              const data = await CustomerService.getAvatar(`${routeId}_${driverId}_${month}_${day}`);
              if (data?.data) {
                return data.data;
              }
            } catch (error) {
              // Try next candidate key.
            }
          }
        }
        return undefined;
      };

      const initialSignature = await tryLoadSignature([
        resolvedDriverId,
        currentRoute?.driver,
        routeSnapshot?.driver
      ], [
        currentRoute?.schedule_date
      ]);
      if (isMounted) {
        setSignature(initialSignature);
      }

      try {
        const requestRes = await SignatureRequestService.getAllSignatureRequests({
          route_id: routeId
        });
        const requestList = requestRes?.data || [];
        const firstRequest = requestList[0];
        if (isMounted) {
          setSignatureRequest(firstRequest);
        }
        if (!initialSignature) {
          const requestDriverIds = requestList.map((item) => item?.driver_id).filter(Boolean);
          const requestDates = requestList.map((item) => item?.route_date).filter(Boolean);
          const signatureFromRequestDriver = await tryLoadSignature(requestDriverIds, requestDates);
          if (isMounted) {
            setSignature(signatureFromRequestDriver);
          }
        }
      } catch (error) {
        if (isMounted) {
          setSignatureRequest(undefined);
        }
      }
    };
    loadSignatureAndRequest();
    return () => {
      isMounted = false;
    };
  }, [dailyTemplateId, currentRoute?.id, currentRoute?.schedule_date, currentRoute?.driver, resolvedDriverId, routeSnapshot?.driver]);
  if (dailyTemplateId && templateLoading) {
    return (
      <div className="list row mb-4 p-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }
  if (dailyTemplateId && templateLoadError) {
    return (
      <div className="list row mb-4 p-4">
        <div className="alert alert-danger">{templateLoadError}</div>
        <button type="button" className="btn btn-link" onClick={() => navigate(`/trans-routes/daily-templates/view/${dailyTemplateId}`)}>
          Back to template
        </button>
      </div>
    );
  }
  if (!currentRoute) {
    return (
      <div className="list row mb-4 p-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }
  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/trans-routes/dashboard">Transportation</Breadcrumb.Item>
          {dailyTemplateId ? (
            <>
              <Breadcrumb.Item href="/trans-routes/daily-templates/list">Daily Route Templates</Breadcrumb.Item>
              <Breadcrumb.Item href={`/trans-routes/daily-templates/view/${dailyTemplateId}`}>
                {templateDoc?.name || "Template"}
              </Breadcrumb.Item>
            </>
          ) : (
            <Breadcrumb.Item href="/trans-routes/dashboard">Transportation Routes</Breadcrumb.Item>
          )}
          <Breadcrumb.Item active>View Route</Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
           <h4>
             View Route Information <button className="btn btn-link btn-sm" onClick={() => {directToDashboad()}}>Back</button>
           </h4> 
        </div>
      </div>
      <div className="app-main-content-list-container form-page">
				<div className="app-main-content-list-func-container">
					<Tabs
            activeKey={routeViewTab}
            onSelect={(key) => setRouteViewTab(key || 'routeOverview')}
            id="route-view-tab"
          >
						<Tab eventKey="routeOverview" title="Route Information">
              <h6 className="text-primary">Route Details {canEditRoutes && <button className="btn btn-sm btn-primary" onClick={() => edit('info')}><Pencil size={16} className="me-2"></Pencil>Edit </button>}</h6>
              <div className="app-main-content-fields-section">
                <div className="field-body">
                  <div className="field-label">Route Name</div> 
                  <div className="field-value">{currentRoute?.name}</div>
                </div>
                <div className="field-body">
                  <div className="field-label">Vehicle</div> 
                  <div className="field-value">{currentVehicle?.vehicle_number}</div>
                </div>
                <div className="field-body">
                  <div className="field-label">Driver</div> 
                  <div className="field-value">{resolvedDriverName || 'Not assigned'}</div>
                </div>
                <div className="field-body">
                  <div className="field-label">Route Type</div> 
                  <div className="field-value">{currentRoute?.type}</div>
                </div>
              </div>
              <div className="app-main-content-fields-section">
                <div className="field-body">
                  <div className="field-label">Route Start Time</div> 
                  <div className="field-value">{formatRouteClockTime(currentRoute?.start_time)}</div>
                </div>
                <div className="field-body">
                  <div className="field-label">Route End Time</div> 
                  <div className="field-value">{formatRouteClockTime(currentRoute?.end_time)}</div>
                </div>
                {currentRoute?.type === 'outbound' &&<div className="field-body">
                  <div className="field-label">Leave Center Time</div> 
                  <div className="field-value">{formatRouteClockTime(currentRoute?.start_time)}</div>
                </div>}
                {currentRoute?.type === 'outbound' &&<div className="field-body">
                  <div className="field-label">Estimated Start Time</div> 
                  <div className="field-value">{formatRouteClockTime(currentRoute?.estimated_start_time)}</div>
                </div>}
              </div>
              <div className="app-main-content-fields-section">
                <div className="field-body">
                  <div className="field-label">Start Mileage</div> 
                  <div className="field-value">{ currentRoute?.start_mileage}</div>
                </div>
                <div className="field-body">
                  <div className="field-label">End Mileage</div> 
                  <div className="field-value">{ currentRoute?.end_mileage}</div>
                </div>
              </div>
              {!dailyTemplateId && (
              <div className="app-main-content-fields-section">
               {signature &&<div className="field-body">
                  <div className="field-label">Driver Signature</div> 
                  <div className="field-value">
                    {signature && <img width="100px" src={`data:image/jpg;base64, ${signature}`}/>}
                    {canEditRoutes && <button className="btn btn-sm btn-outline-danger ms-2" onClick={openResetSignatureConfirmModal}>Reset Signature</button>}
                  </div>
                </div>}
                {!signature && !isSignatureRequestActive && <div className="field-body">
                  <div className="field-label">Signature Request</div> 
                  <div className="field-value"><button className="btn btn-sm btn-primary" onClick={() => generateSignatureRequest()}>Generate Signature Link For Driver</button></div>
                </div>}
                {!signature && isSignatureRequestActive &&  <div className="alert alert-success fade show mb-2 mt-2" role="alert">
                  <div>Please send this to the driver to get signature:</div>
                  <div>{`${window.location.origin}/signature/${signatureRequest?.id}`}</div>
                </div>}
              </div>
              )}
              <div className="app-main-content-fields-section">
                <div className="field-body">
                  <div className="field-label">Checklist</div> 
                  <div className="field-value">
                    {routeForChecklistDisplay && mergedChecklistForDisplay?.length > 0 && mergedChecklistForDisplay.map((item, idx) => (
                      <div key={`${item?.item}-${idx}`}>{`${item?.item}: ${item?.result ? 'Yes' : 'No'}`}</div>
                    ))}
                    {routeForChecklistDisplay && (!mergedChecklistForDisplay || mergedChecklistForDisplay.length === 0) && (
                      <>No Checklist found</>
                    )}
                  </div>
                </div>
              </div>
              <RouteCustomerEditor
                currentRoute={routeForAssignmentView}
                viewMode={true}
                editFun={edit}
                canEdit={canEditRoutes}
                scheduledAbsentCustomerIds={leaveCustomerIds}
              />
            </Tab>
            {!dailyTemplateId && (
            <Tab eventKey="routeStatus" title="Route Status">
            <div className="list row">
              <div className="col-md-12 mb-3">
                <div className="app-main-content-fields-section">
                  <div className="field-body">
                    <div className="field-label">Route Status</div>
                    <div className="field-value d-flex align-items-center gap-2">
                      <select
                        className="form-select"
                        style={{ width: '260px' }}
                        value={routeStatusValue}
                        onChange={(e) => setRouteStatusValue(e.target.value)}
                        disabled={isSavingRouteStatus || !canEditRoutes}
                      >
                        {routeStatusOptions.map((option) => (
                          <option key={option.value || 'not-started'} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={saveRouteStatus}
                        disabled={isSavingRouteStatus || !canEditRoutes}
                      >
                        {isSavingRouteStatus ? 'Saving...' : 'Save Status'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-12 mb-4">
                {routeForStatusView && <PersonnelSection transRoutes={[routeForStatusView]} showCompletedInfo={true} showGroupInfo={true} isInbound={routeForStatusView?.type === 'inbound' } allowForceEdit={canEditRoutes} sectionName="Personnel Status (click on each user to edit)" relatedOutbound={getRelatedOutboundRoutesForThisView()} vehicle={currentVehicle} driverName={resolvedDriverName} deleteFile={deleteFile} onRouteUpdated={refreshRouteStatusData} groupActionsPortalTarget={groupActionsHost} groupActionsUsePortal={true}/>}
              </div>
            </div>
            </Tab>
            )}
          </Tabs>
          {!dailyTemplateId && routeViewTab === 'routeStatus' && (
            <div className="list-func-panel" ref={setGroupActionsHost} />
          )}
        </div>
        <Modal show={showVehicleDetails} onHide={() => closeModal()}>
        <Modal.Header closeButton>
          <Modal.Title>Vehicle Info</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <>
            <div className="mb-2">Vehicle Number: {currentVehicle?.vehicle_number}</div>
            <div className="mb-2">Tag: {currentVehicle?.tag}</div>
            <div className="mb-2">EzPass: {currentVehicle?.ezpass}</div>
            <div className="mb-2">GPS: {currentVehicle?.gps_tag}</div>
            <div className="mb-2">Capacity: {currentVehicle?.capacity}</div>
            <div className="mb-2">Status: {currentVehicle?.status}</div>
            <div className="mb-2">Mileage: {currentVehicle?.mileage}</div>
          </>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => closeModal()}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showResetSignatureConfirmModal} onHide={closeResetSignatureConfirmModal}>
        <Modal.Header closeButton={!isResettingSignature}>
          <Modal.Title>Reset Driver Signature</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to reset this signature? The driver will need to sign again.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeResetSignatureConfirmModal} disabled={isResettingSignature}>
            Cancel
          </Button>
          <Button variant="danger" onClick={resetSignatureForResign} disabled={isResettingSignature}>
            {isResettingSignature ? 'Resetting...' : 'Confirm Reset'}
          </Button>
        </Modal.Footer>
      </Modal>
      </div>
     
      

    </>
  );
};

export default RouteView;